'use client'

import { useCallback, useState } from 'react'
import { AppError, ErrorHandler, AuthError } from '@repo/errors'

export interface UseErrorHandlerOptions {
  onError?: (error: AppError) => void
  onAuthError?: () => void
  showToast?: (message: string, type: 'error' | 'warning') => void
}

export function useErrorHandler(options: UseErrorHandlerOptions = {}) {
  const [error, setError] = useState<AppError | null>(null)
  const [isError, setIsError] = useState(false)

  const handleError = useCallback((error: unknown) => {
    // Normalize the error
    const appError = ErrorHandler.normalize(error)
    
    // Update state
    setError(appError)
    setIsError(true)
    
    // Log the error
    ErrorHandler.log(appError)
    
    // Get user-friendly message
    const userMessage = ErrorHandler.getUserMessage(appError)
    
    // Show toast if function provided
    if (options.showToast) {
      const toastType = appError.isOperational ? 'warning' : 'error'
      options.showToast(userMessage, toastType)
    }
    
    // Handle auth errors specifically
    if (appError instanceof AuthError && options.onAuthError) {
      options.onAuthError()
    }
    
    // Call custom error handler
    if (options.onError) {
      options.onError(appError)
    }
    
    return appError
  }, [options])

  const clearError = useCallback(() => {
    setError(null)
    setIsError(false)
  }, [])

  return {
    error,
    isError,
    handleError,
    clearError,
    errorMessage: error ? ErrorHandler.getUserMessage(error) : null
  }
}

// Convenience hook for async operations with error handling
export function useAsyncError() {
  const { handleError } = useErrorHandler()

  const throwError = useCallback((error: unknown) => {
    handleError(error)
    throw error
  }, [handleError])

  return throwError
}